import { List, fromJS } from 'immutable';
import isCurrentPurchase from 'utils/purchase/validateCurrentPurchaseByTrip';
import getSeats from 'utils/purchase/getSeats';
import * as types from 'constants/ActionTypes';
// eslint-disable-next-line import/no-cycle
import purchaseAllowsSeatSelection from 'utils/purchase/purchaseAllowsSeatSelection';
import {
  getPurchaseSelectedSeats,
  getUpdatedSeats,
  preventSelectPetFriendly,
  reservationHasSelectedSeats,
} from 'utils/seats';
import wayIsOpenTicket from 'utils/wayIsOpenTicket';
import { handleSetPassengers } from '../features/purchase/redux/handlers';
import { defaultPurchase } from '../store';

/**
 * Get the connection trips from the given trip fragments.
 *
 * @param {*} fragments - trip fragments
 * @returns {Array} - Connection trips
 */
const getConnectionTrips = (fragments = []) => {
  /**
   * If the trip has connections, the first fragment is the general trip and the rest are connections.
   * The slice removes the general trip from the list.
   * The minimum number of connections is 1 (so we'll have at least three fragments).
   *  1. General trip (a -> c)
   *  2. Fist trip (a -> b)
   *  3. Second trip (b -> c)
   *
   * So, our fallback number for the slice is 3.
   */
  return fragments.slice(1, fragments.length || 3) || [];
};

/**
 * Build the selected installments plan object from payment plans data.
 * @param {string} card - Card type (e.g., 'visaMc', 'amex').
 * @param {number} months - Number of installments.
 * @param {object} paymentPlans - Available payment plans.
 * @returns {object|null} The installments plan object or null if not available.
 */
const buildSelectedInstallmentsPlan = (card, months, paymentPlans) => {
  if (months > 1 && card && paymentPlans?.[card]?.[months]) {
    const planData = paymentPlans[card][months];
    const total =
      planData.total || (planData.monthlyPayment ? months * planData.monthlyPayment : 0);
    return { ...planData, card, months, total };
  }
  return null;
};

export default function purchase(purchase = {}, action) {
  switch (action.type) {
    case types.SET_SELECTED_PLAN:
      return purchase.set('selectedPlan', action.selectedPlan);

    case types.SET_INSTALLMENTS_CARD:
      return purchase.set('selectedInstallmentsCard', action.card);

    case types.EXPIRE_PURCHASE:
      return purchase.set('isExpired', true);

    case types.SET_FAILED_STATE_PURCHASE:
      return purchase.set('state', 'failed');

    case types.REQUEST_PURCHASE:
      return purchase.set('isFetching', true).set('isFetchingRedirect', true);

    case types.RECEIVE_PURCHASER:
      return purchase.merge({
        ...action.purchaser,
        isUpdating: false,
        loaded: true,
      });

    case types.SET_STORE_REFERENCE_ISSUED:
      // AM-1686: targeted refresh of just this flag after a store payment attempt, so the
      // QuickCheckout edit guard sees the newly-issued reference without a full (heavy)
      // purchase reload that would disrupt the payment-failed state.
      return purchase.set('storeReferenceIssued', action.value);

    case types.RECEIVE_PURCHASE: {
      /**
       * Avoid to merge into state if token has changed.
       * This behavior is not expected since requests can be overrode and the action purchase could be an old state.
       * Issue: https://reservamossaas.atlassian.net/browse/ENT-389
       */
      if (purchase.get('token') && purchase.get('token') !== action.purchase.token) {
        return purchase;
      }

      const loadingOwners = purchase.get('quickPurchaseLoadingOwners');
      const currentUpdateOwner = purchase.getIn(['quickPurchaseLoadingOwners', 'update']);
      const hasPendingOwnedWayLoading = ['departs', 'returns'].some((way) => {
        const detailsOwner = purchase.getIn(['quickPurchaseLoadingOwners', 'details', way]);
        const ticketsOwner = purchase.getIn(['quickPurchaseLoadingOwners', 'tickets', way]);
        return (
          (detailsOwner != null && purchase.getIn([way, 'fetchingDetails'])) ||
          (ticketsOwner != null && purchase.getIn([way, 'isLockingTickets']))
        );
      });
      const completesOwnedUpdate =
        action.loadingOwnerId != null && action.loadingOwnerId === currentUpdateOwner;

      const purchaseHasTickets = (rawPurchase) => {
        if (action.tenantSupportsConnections) {
          return (
            rawPurchase.departs.tickets.every((ticket) => ticket?.state === 'locked') &&
            (!rawPurchase.returns ||
              rawPurchase.returns.tickets.every((ticket) => ticket?.state === 'locked'))
          );
        }

        return (
          rawPurchase.departs.fragments.every(
            (fragment) =>
              fragment.tickets.length > 0 &&
              fragment.tickets.every((ticket) => ticket?.state === 'locked'),
          ) &&
          (!rawPurchase.returns ||
            rawPurchase.departs.fragments.every(
              (fragment) =>
                fragment.tickets.length > 0 &&
                fragment.tickets.every((ticket) => ticket?.state === 'locked'),
            ))
        );
      };

      const currentPaymentOption = purchase.get('selectedPaymentOption');
      // selectedPaymentMethod is stored as a plain JS object, so read its engine
      // directly to preserve the user's explicit choice across backend refreshes.
      const selectedMethodEngine = purchase.get('selectedPaymentMethod')?.engine;

      let paymentType = action.purchase.availablePayments.includes('reservamos_pay')
        ? 'reservamos_pay'
        : currentPaymentOption;

      if (action.purchase.availablePayments.includes('free_pay')) {
        paymentType = 'free_pay';
      }

      const selectedInstallmentsCard = purchase.get('selectedInstallmentsCard');
      const months = action.purchase.monthlySelectedPlan;
      const { paymentPlans } = action.purchase;

      // Get local state of selected installments plan
      const localSelectedPlanOption = purchase.get('monthlySelectedPlan');

      const newSelectedInstallmentsPlan = buildSelectedInstallmentsPlan(
        selectedInstallmentsCard,
        months,
        paymentPlans,
      );

      /**
       * Trip with connections props
       *
       * This props are needed when the purchase has connections and we load the app from passengers/checkout pages.
       * This means that we are not fetching the trips details, which also calculates the connection props.
       */
      const departureHasConnections = action.purchase.departs?.fragments.length > 1;
      const returnHasConnections = action.purchase.returns?.fragments.length > 1;
      const departureConnectionTrips = getConnectionTrips(action.purchase.departs?.fragments);
      const returnConnectionTrips = getConnectionTrips(action.purchase.returns?.fragments);
      // ----------------------------

      const preSelectedSeats = action.purchase.preSelectedSeats || {};
      const { departs, returns } = purchase?.toJS();

      const hasPreSelectedSeats = Object.keys(preSelectedSeats).length;
      const selectedSeats = getPurchaseSelectedSeats({
        purchaseData: action.purchase,
        currentSelectedSeats: {
          depart: departs?.selectedSeats || [],
          return: returns?.selectedSeats || [],
        },
      });
      let newPurchaseState = purchase
        .mergeDeep(
          fromJS({
            ...action.purchase,
            isFetching: false,
            isFetchingRedirect: false,
            isUpdating: false,
            loaded: true,
            hasTickets: purchaseHasTickets(action.purchase),
            allowsSeatSelection: purchaseAllowsSeatSelection(action.purchase),
            lastUpdated: action.receivedAt,
            hasSelectedSeats: hasPreSelectedSeats && Boolean(Object.keys(selectedSeats).length),
            ...(localSelectedPlanOption > 1 && { monthlySelectedPlan: localSelectedPlanOption }),
            ...(newSelectedInstallmentsPlan && {
              selectedInstallmentsPlan: newSelectedInstallmentsPlan,
            }),
            departs: {
              ...action.purchase.departs,
              selectedSeats: selectedSeats.departure,
              hasConnections: departureHasConnections,
              trips: departureConnectionTrips,
            },
            returns: {
              ...action.purchase.returns,
              selectedSeats: selectedSeats.return,
              hasConnections: returnHasConnections,
              trips: returnConnectionTrips,
            },
            preSelectedSeats,
          }),
        )
        /**
         * Estos valores se establecen por separado para evitar que con mergeDeep se dupliquen valores al ser arrays
         * Ej.: paymentMethods: [{type:'oxxo'},{type: 'coppel_pay}] -> paymentMethods: [{type:'coppel_pay'},{type: 'coppel_pay}]
         * Esto porque en action.purchase se recibe paymentMethods: [{type:'coppel_pay'}] haciendo merge en la posición 0 y dejando coppel también en la pos 1
         *
         * Otro ejemplo es el caso de fragments, si se editan los asientos se mantienen los tickets anteriores aunque se hayan borrado
         */
        .set('availablePayments', fromJS(action.purchase.availablePayments)) // TODO remove this line when we remove the old payment methods
        .set('paymentMethods', fromJS(action.purchase.paymentMethods))
        .set('selectedPaymentOption', paymentType)
        // Keep the engine the user already chose (e.g. Stripe) so a backend
        // refresh (adding coverage, etc.) does not revert it to the tenant default.
        // Otherwise prefer API engine when present so default placeholder does not
        // override backend value (bulk and other flows that hit checkout right after create).
        .set(
          'paymentEngine',
          selectedMethodEngine || action.purchase.paymentEngine || purchase.get('paymentEngine'),
        )
        .set('passengerSelection', fromJS(action.purchase.passengerSelection))
        .setIn(['departs', 'fragments'], fromJS(action.purchase.departs.fragments))
        .update((newState) => {
          // Override return fragments if they are present in the returns way
          if (purchase.getIn(['returns', 'fragments'])) {
            return newState.setIn(
              ['returns', 'fragments'],
              fromJS(action.purchase.returns.fragments),
            );
          }
          return newState;
        });

      // A same-token purchase refresh is data enrichment, not cancellation of independently
      // pending detail/ticket work. Keep every client-only flag paired with its reducer owner.
      if (loadingOwners) {
        newPurchaseState = newPurchaseState.set('quickPurchaseLoadingOwners', loadingOwners);
      }
      if (currentUpdateOwner != null) {
        if (completesOwnedUpdate && !hasPendingOwnedWayLoading) {
          newPurchaseState = newPurchaseState
            .set('isUpdating', false)
            .setIn(['quickPurchaseLoadingOwners', 'update'], null);
        } else {
          newPurchaseState = newPurchaseState
            .set('isUpdating', purchase.get('isUpdating'))
            .setIn(['quickPurchaseLoadingOwners', 'update'], currentUpdateOwner);
        }
      }

      return newPurchaseState;
    }

    case types.RESET_PURCHASE:
      return defaultPurchase.getInstance().set('lastUpdated', action.receivedAt);

    case types.UPDATE_PURCHASE: {
      const ownerPath = ['quickPurchaseLoadingOwners', 'update'];
      const loadingOwnerId = action.loadingOwnerId ?? null;
      if (
        !action.updating &&
        loadingOwnerId !== null &&
        purchase.getIn(ownerPath) !== loadingOwnerId
      ) {
        return purchase;
      }
      return purchase
        .set('isUpdating', action.updating)
        .setIn(ownerPath, action.updating ? loadingOwnerId : null);
    }

    case types.UPDATE_PURCHASE_TOTAL:
      return purchase.set('total', action.total);

    case types.UPDATE_PAYMENT_PLANS: {
      let updatedPurchase = purchase.set('paymentPlans', action.paymentPlans);

      // Update selectedInstallmentsPlan if there's an active plan selected
      const currentPlan = purchase.get('selectedInstallmentsPlan')?.toJS?.() || {};
      const { card, months } = currentPlan;
      const updatedPlan = buildSelectedInstallmentsPlan(card, months, action.paymentPlans);
      if (updatedPlan) {
        updatedPurchase = updatedPurchase.set('selectedInstallmentsPlan', fromJS(updatedPlan));
      }

      return updatedPurchase;
    }

    case types.SET_BUS_CATEGORIES: {
      if (action.tripSlug && !isCurrentPurchase(purchase, action)) {
        return purchase;
      }

      return purchase.set('busCategories', action.busCategories);
    }

    case types.SET_SEAT_MAP: {
      if (!isCurrentPurchase(purchase, action)) {
        return purchase;
      }

      const {
        layout,
        diagramType,
        layouts = {},
        seats,
        trips = [],
        seatPriceZones = [],
        tripPricingFields = {},
      } = action;
      return purchase.mergeIn([action.way], {
        layouts,
        layout: fromJS(layout),
        seats: seats || getSeats(action.layout),
        diagramType,
        trips,
        seatPriceZones,
        hasConnections: trips.length > 1,
        // Trip-details pricing (incl. any BFO pricingContext) for this way, so the
        // authoritative purchase-scoped context can be reconciled after the fetch.
        tripPricingFields: fromJS(tripPricingFields),
      });
    }

    case types.REQUEST_TRIPS_DETAILS: {
      const loadingOwnerId = action.loadingOwnerId ?? null;
      return purchase
        .set('isFetchingDetails', true)
        .setIn([action.way, 'fetchingDetails'], true)
        .setIn(['quickPurchaseLoadingOwners', 'details', action.way], loadingOwnerId);
    }

    case types.RECEIVE_TRIPS_DETAILS: {
      if (!isCurrentPurchase(purchase, action)) {
        return purchase;
      }
      const ownerPath = ['quickPurchaseLoadingOwners', 'details', action.way];
      const loadingOwnerId = action.loadingOwnerId ?? null;
      if (loadingOwnerId !== null && purchase.getIn(ownerPath) !== loadingOwnerId) {
        return purchase;
      }

      const updatedPurchase = purchase
        .mergeIn([action.way], {
          fetchingDetails: false,
          hasDetails: true,
        })
        .setIn(ownerPath, null);
      const isFetchingDetails =
        updatedPurchase.getIn(['departs', 'fetchingDetails']) ||
        updatedPurchase.getIn(['returns', 'fetchingDetails']);
      const hasDetails =
        updatedPurchase.getIn(['departs', 'hasDetails']) &&
        updatedPurchase.getIn(['returns', 'hasDetails']);
      return updatedPurchase.merge({
        isFetchingDetails: Boolean(isFetchingDetails),
        hasDetails: Boolean(hasDetails),
      });
    }

    case types.INVALIDATE_PURCHASE_TRIPS_DETAILS: {
      let updatedPurchase = purchase;
      (action.ways || []).forEach((way) => {
        [
          'layout',
          'layouts',
          'seats',
          'diagramType',
          'trips',
          'seatPriceZones',
          'tripPricingFields',
        ].forEach((field) => {
          updatedPurchase = updatedPurchase.deleteIn([way, field]);
        });
        updatedPurchase = updatedPurchase.mergeIn([way], {
          fetchingDetails: false,
          hasDetails: false,
        });
      });

      const isFetchingDetails = ['departs', 'returns'].some((way) => {
        return updatedPurchase.getIn([way, 'fetchingDetails']);
      });
      return updatedPurchase.set('hasDetails', false).set('isFetchingDetails', isFetchingDetails);
    }

    case types.PURCHASE_POST_PASSENGERS:
      return purchase.set('savingPassengers', true);

    case types.PURCHASE_SET_PASSENGERS: {
      const { passengers, token } = action;
      /**
       * Set passengers only if the purchase token is still the same as the request.
       * This behavior is not expected since requests can be overrode and the action purchase could be an old state.
       * Issue: https://reservamossaas.atlassian.net/browse/ENT-389
       */
      if (purchase.get('token') === token) {
        return handleSetPassengers(purchase, passengers);
      }
      return purchase;
    }

    case types.FINISHED_SEAT_SELECTION: {
      return purchase
        .set('savingPassengers', false)
        .set('passengers', fromJS(action.passengersWithSeats));
    }

    case types.PURCHASE_SET_PAYMENT_CARDS: {
      return purchase.merge({
        purchaserPaymentCards: action.cards,
      });
    }

    case types.FETCHING_TAXPAYER_ID:
      return purchase.set('fetchingTaxpayerId', action.updating);

    case types.PURCHASE_SET_PAYMENT_PLANS: {
      const anyPlan = Boolean(Object.keys(action.paymentPlans).length);
      let updatedPurchase = purchase
        .set('paymentPlans', action.paymentPlans)
        .set('qualifiesForMonthlyInstallments', anyPlan);

      // Update selectedInstallmentsPlan if there's an active plan selected
      const currentPlan = purchase.get('selectedInstallmentsPlan')?.toJS?.() || {};
      const { card, months } = currentPlan;
      const updatedPlan = buildSelectedInstallmentsPlan(card, months, action.paymentPlans);
      if (updatedPlan) {
        updatedPurchase = updatedPurchase.set('selectedInstallmentsPlan', fromJS(updatedPlan));
      }

      return updatedPurchase;
    }

    case types.UPDATE_SEAT: {
      // Update the selected seats in the purchase state

      const { seat, way } = action;
      const selectedSeats = purchase.getIn([way, 'selectedSeats']);
      const newSelectedSeats = selectedSeats.toJS();
      const updatedSeats = getUpdatedSeats({ selectedSeats: newSelectedSeats, seat });
      const updatedPurchase = purchase.setIn([way, 'selectedSeats'], List(updatedSeats));

      return updatedPurchase;
    }

    case types.SELECT_SEATS: {
      const seats = preventSelectPetFriendly(action.seats);

      const updatedPurchase = purchase.setIn([action.way, 'selectedSeats'], List(seats));
      let hasSelectedSeats = reservationHasSelectedSeats(updatedPurchase.get('departs'));

      if (
        updatedPurchase.get('roundTrip') &&
        !wayIsOpenTicket(updatedPurchase.get('returns').toJS())
      ) {
        hasSelectedSeats =
          hasSelectedSeats && reservationHasSelectedSeats(updatedPurchase.get('returns'));
      }

      return updatedPurchase.merge({ hasSelectedSeats });
    }

    case types.REQUEST_TICKETS: {
      const loadingOwnerId = action.loadingOwnerId ?? null;
      const updatedPurchase = purchase
        .setIn([action.way, 'isLockingTickets'], true)
        .setIn(['quickPurchaseLoadingOwners', 'tickets', action.way], loadingOwnerId);
      return updatedPurchase
        .set('isLockingTickets', true)
        .set('isUpdating', true)
        .setIn(['quickPurchaseLoadingOwners', 'update'], loadingOwnerId);
    }

    case types.RECEIVE_TICKETS: {
      const ownerPath = ['quickPurchaseLoadingOwners', 'tickets', action.way];
      const loadingOwnerId = action.loadingOwnerId ?? null;
      if (loadingOwnerId !== null && purchase.getIn(ownerPath) !== loadingOwnerId) {
        return purchase;
      }
      let ticketsErrorType;
      if (action.error) {
        ticketsErrorType = /occupied/g.test(action.error) ? 'occupied' : 'other';
      }
      const roundTrip = purchase.get('roundTrip');
      const updatedPurchase = purchase
        .mergeIn([action.way], {
          isLockingTickets: false,
          tickets: List(action.tickets),
        })
        .setIn(ownerPath, null);

      return updatedPurchase.merge({
        hasTickets:
          updatedPurchase.getIn(['departs', 'tickets']).size > 0 &&
          (!roundTrip || updatedPurchase.getIn(['returns', 'tickets']).size > 0),

        isLockingTickets: ['departs', 'returns'].some((way) => {
          const newLockingTickets = updatedPurchase.getIn([way, 'isLockingTickets']);
          return newLockingTickets;
        }),
        ticketsErrorType,
      });
    }

    case types.RELEASE_QUICK_PURCHASE_LOADING: {
      const { loadingOwnerId } = action;
      if (loadingOwnerId === null || loadingOwnerId === undefined) return purchase;

      let updatedPurchase = purchase;
      ['departs', 'returns'].forEach((way) => {
        const detailsOwnerPath = ['quickPurchaseLoadingOwners', 'details', way];
        if (updatedPurchase.getIn(detailsOwnerPath) === loadingOwnerId) {
          updatedPurchase = updatedPurchase
            .setIn([way, 'fetchingDetails'], false)
            .setIn(detailsOwnerPath, null);
        }

        const ticketsOwnerPath = ['quickPurchaseLoadingOwners', 'tickets', way];
        if (updatedPurchase.getIn(ticketsOwnerPath) === loadingOwnerId) {
          updatedPurchase = updatedPurchase
            .setIn([way, 'isLockingTickets'], false)
            .setIn(ticketsOwnerPath, null);
        }
      });

      const updateOwnerPath = ['quickPurchaseLoadingOwners', 'update'];
      if (updatedPurchase.getIn(updateOwnerPath) === loadingOwnerId) {
        updatedPurchase = updatedPurchase.set('isUpdating', false).setIn(updateOwnerPath, null);
      }

      const isFetchingDetails = Boolean(
        updatedPurchase.getIn(['departs', 'fetchingDetails']) ||
          updatedPurchase.getIn(['returns', 'fetchingDetails']),
      );
      const isLockingTickets = Boolean(
        updatedPurchase.getIn(['departs', 'isLockingTickets']) ||
          updatedPurchase.getIn(['returns', 'isLockingTickets']),
      );
      return updatedPurchase
        .set('isFetchingDetails', isFetchingDetails)
        .set('isLockingTickets', isLockingTickets);
    }
    case types.BFO_RESELECTION_READY:
      return purchase
        .set('bfoReselectionStatus', 'ready')
        .set('ticketsErrorType', 'bfo_price_refresh_required');

    case types.BFO_RESELECTION_REFRESHING:
      return purchase
        .set('bfoReselectionStatus', 'refreshing')
        .set('bfoReselectionWays', fromJS(action.ways || []))
        .set('ticketsErrorType', null);

    case types.BFO_RESELECTION_BLOCKED:
      return purchase.set('bfoReselectionStatus', 'blocked').set('ticketsErrorType', null);

    case types.BFO_OPAQUE_RECOVERY_ATTEMPTED:
      return purchase.set('bfoOpaqueRecoveryAttempted', true);

    case types.CLEAR_TICKETS_ERROR_TYPE: {
      return purchase.merge({
        ticketsErrorType: null,
        bfoReselectionStatus: null,
      });
    }

    case types.TOGGLE_INSURANCE:
      return purchase.set('updatingInsurance', action.updating);

    case types.UPDATE_USING_WALLET:
      return purchase.set('updatingWallet', action.updating);

    case types.REQUEST_DISCOUNT:
      return purchase.set('applyingDiscount', true);

    case types.RECEIVE_DISCOUNT:
      return purchase.set('applyingDiscount', false);

    case types.SELECT_PAYMENT_OPTION:
      return purchase.set('selectedPaymentOption', action.option);

    case types.SELECT_PAYMENT_METHOD: {
      const updated = purchase.set('selectedPaymentMethod', action.method);
      const hasEngine = Object.prototype.hasOwnProperty.call(action.method || {}, 'engine');
      return updated.set('paymentEngine', hasEngine ? action.method.engine : null);
    }

    case types.SELECT_MONTHLY_PLAN: {
      const plan = action.installmentsPlan;

      // Validate plan has required data
      if (!plan || !plan.months) {
        return purchase;
      }

      if (Number(plan.months) <= 1) {
        return purchase.merge({
          selectedInstallmentsPlan: { ...plan, months: 1, total: 0 },
          monthlySelectedPlan: 1,
        });
      }

      if (!plan.monthlyPayment) return purchase;

      const { months, monthlyPayment } = plan;
      const total = plan.total || months * monthlyPayment;
      const selectedInstallmentsCard = plan.card || purchase.get('selectedInstallmentsCard');

      return purchase.merge({
        selectedInstallmentsCard,
        selectedInstallmentsPlan: { ...plan, total },
        monthlySelectedPlan: months,
      });
    }

    case types.SET_ENABLE_MONTHLY_SELECTED_PLAN:
      return purchase.set('enableMonthlySelectedPlan', action.enableMonthlySelectedPlan);

    case types.PURCHASE_SET_EXPIRATION:
      return purchase.set('expiresAt', action.expiration);

    case types.PURCHASE_EXTENDED_EXPIRATION: {
      const expirationExtension = purchase.get('expirationExtension').toJS();
      return purchase
        .set('expirationExtension', {
          ...expirationExtension,
          extendedAt: action.extendedAt,
          available: false,
        })
        .set('expiresAt', action.expiresAt)
        .set('currentTime', action.currentTime);
    }

    case types.SET_EMAIL_STATUS:
      return purchase.merge({
        sentEmailStatus: action.status,
      });

    case types.PURCHASE_INSURANCES_CHECKED:
      return purchase.set('insurancesChecked', true);

    case types.SET_EXCHANGE_OPERATION:
      return purchase.set('isExchange', true);

    case types.TOGGLE_CARBON_OFFSET:
      return purchase.set('updatingCarbonOffset', action.updating);

    case types.UPDATING_WALLET_TYPE:
      return purchase.set('isUpdatingWalletType', action.updating);

    case types.RECEIVE_WALLET_TYPE:
      return purchase
        .set('walletType', action.walletType)
        .set('flatFareAvailable', action.flatFareAvailable);

    case types.TOGGLE_FLAT_FARE:
      return purchase.set('isUpdatingFlatFare', action.updating);

    case types.SET_YUNO_SESSION_DATA:
      return purchase.set('yuno', action.yunoSessionData);

    case types.REQUEST_NEXT_DEPARTURES:
      return purchase.merge({
        departures: {
          isLoading: true,
        },
      });

    case types.RECEIVE_NEXT_DEPARTURES:
      return purchase.merge({
        departures: {
          ...fromJS(action.nextDepartures).toJS(),
          isLoading: false,
        },
      });

    case types.UPDATE_SERVICE_LEVEL:
      return purchase.set('updatingServiceLevel', action.updating);

    case types.RECEIVE_PROMO_CODE:
      return purchase.set('promoCodeApplied', action.success);

    case types.REQUEST_PROMO_CODE:
      return purchase.set('promoCodeApplied', false);

    case types.VALIDATE_CHANGE_DOCUMENT_LOADING:
      return purchase
        .set('validateChangeDocumentLoading', action.isLoading)
        .set('validateChangeDocumentData', null)
        .set('validateChangeDocumentError', null);

    case types.VALIDATE_CHANGE_DOCUMENT_RECEIVE:
      return purchase
        .set('validateChangeDocumentData', action.data)
        .set('validateChangeDocumentLoading', false)
        .set(
          'validateChangeDocumentError',
          action.data.success === false ? action.data.message : null,
        );

    case types.CHANGE_DOCUMENT_LOADING:
      return purchase
        .set('changeDocumentLoading', action.isLoading)
        .set('changeDocumentData', null)
        .set('changeDocumentError', null);

    case types.CHANGE_DOCUMENT_RECEIVE:
      return purchase
        .set('changeDocumentData', action.data)
        .set('changeDocumentLoading', false)
        .set('changeDocumentError', action.data.success === false ? action.data.message : null);

    case types.REQUEST_PAYPAL_ELIGIBILITY:
      return purchase
        .setIn(['paypalEligibility', 'isLoading'], true)
        .setIn(['paypalEligibility', 'error'], null);

    case types.RECEIVE_PAYPAL_ELIGIBILITY:
      return purchase.set(
        'paypalEligibility',
        fromJS({
          isChecked: true,
          isLoading: false,
          methods: action.payload.methods,
          error: null,
        }),
      );

    case types.PAYPAL_ELIGIBILITY_FAILURE:
      return purchase
        .setIn(['paypalEligibility', 'isChecked'], true)
        .setIn(['paypalEligibility', 'isLoading'], false)
        .setIn(['paypalEligibility', 'error'], action.error);

    default:
      return purchase;
  }
}
